import React, { memo, useCallback, useMemo } from 'react';
import { StyleSheet, TouchableHighlight, View } from 'react-native';
import { useAppSelector } from 'store/hooks';
import Amount from 'design-system/Amount';
import EmojiText from 'design-system/EmojiText';
import { CIRCLE_SIZE } from 'design-system/LogoBankAccount';
import Spacer from 'design-system/Spacer';
import Text from 'design-system/Text';
import { rem } from 'design-system/values';
import useColors from 'hooks/useColors';
import RowAccountGoalInfo from 'features/accounts/components/RowAccountGoalInfo';
import useShouldShowUpgrade from 'features/premium/hooks/useShouldShowUpgrade';
import { useMainStackNavigation } from 'utils/types/navigationV6';
import { selectActivePotInterest } from 'features/pots/reducers/selectors';
import { getBalanceForPots } from 'utils/api/getBalance';
import { SpaceUserAvatar } from 'design-system/SpaceUserAvatar';
import RowAccountGoalProgress from './RowAccountGoalProgress';
import { isWeb } from '../../../constants';
const RowPot = ({ data, disabled, onDark }) => {
    const activePotInterest = useAppSelector((store) => selectActivePotInterest(store, data.productId));
    const { primaryBalance, secondaryBalance } = useMemo(() => getBalanceForPots(data), [data]);
    const colors = useColors();
    const navigation = useMainStackNavigation();
    const handlePress = useCallback(() => {
        navigation.navigateDeprecated('Pot', { screenParams: data });
    }, [data]);
    const { isUnlocked } = useShouldShowUpgrade({
        benefitId: 'savingGoals',
    });
    const hasGoal = data.goal && isUnlocked;
    const description = useMemo(() => {
        if ((data.type === 'INTEREST' ||
            data.type === 'ACCUMULATED_INTEREST' ||
            data.type === 'INTEREST_NOTICE' ||
            data.type === 'ACCUMULATED_INTEREST_NOTICE') &&
            activePotInterest) {
            return (<Text TextThin-14 Secondary numberOfLines={1}>
          {activePotInterest}%{' AER'}
        </Text>);
        }
        return null;
    }, [activePotInterest, data.type]);
    const goalText = useMemo(() => {
        if (hasGoal) {
            if ((data.type === 'INTEREST' || data.type === 'INTEREST_NOTICE') &&
                activePotInterest) {
                return (<View style={styles.row}>
            <Text TextThin-14 numberOfLines={1}>
              <Text TextThin-14 Secondary>
                {activePotInterest}%
              </Text>
              <Text TextThin-14 Secondary style={styles.alignCenter}>
                {` AER •`}&nbsp;
              </Text>
            </Text>
            <RowAccountGoalInfo pot={data}/>
          </View>);
            }
            return <RowAccountGoalInfo pot={data}/>;
        }
        return null;
    }, [activePotInterest, data, hasGoal]);
    const goalOrDescription = goalText || description;
    const SpaceUserInfo = useMemo(() => (<View style={styles.row}>
        <View style={styles.flex}>
          <SpaceUserAvatar xxSmall spaceId={data.spaceId} userId={data.userId} style={styles.spaceUserAvatar}/>
        </View>
        {!goalOrDescription && !!secondaryBalance && (<View style={styles.amountContainer}>
            <Amount NumbersThin-14 Secondary value={secondaryBalance}/>
          </View>)}
      </View>), [data.spaceId, data.userId, goalOrDescription, secondaryBalance]);
    return (<TouchableHighlight underlayColor={colors.background.underlay} activeOpacity={1} onPress={handlePress} disabled={disabled || isWeb}>
      <View style={styles.container}>
        <View style={[
            styles.iconContainerCircle,
            {
                backgroundColor: onDark
                    ? colors.background.light
                    : colors.background.dark,
            },
        ]}>
          <EmojiText fontSize={20}>{data.emoji}</EmojiText>
        </View>
        <Spacer.W16 />
        <View style={styles.flex}>
          <View style={styles.row}>
            <View style={styles.flex}>
              <Text Text-16 numberOfLines={1}>
                {data.name}
              </Text>
            </View>
            <Spacer.W8 />
            <Amount Numbers-16 value={primaryBalance}/>
          </View>
          {goalOrDescription ? (<View style={styles.row}>
              <View style={styles.flex}>{goalOrDescription}</View>
              {!!secondaryBalance && (<View style={styles.amountContainer}>
                  <Amount NumbersThin-14 Secondary value={secondaryBalance}/>
                </View>)}
            </View>) : null}
          {!hasGoal && SpaceUserInfo}
          <RowAccountGoalProgress pot={data} containerStyles={styles.progress}/>
          {!!hasGoal && (<>
              <Spacer h={2}/>
              {SpaceUserInfo}
            </>)}
        </View>
      </View>
    </TouchableHighlight>);
};
const styles = StyleSheet.create({
    flex: {
        flex: 1,
    },
    container: {
        padding: rem(16),
        flexDirection: 'row',
        alignItems: 'center',
        ...(isWeb && {
            paddingHorizontal: 8,
        }),
    },
    amountContainer: {
        marginLeft: rem(8),
        alignItems: 'flex-end',
        justifyContent: 'center',
    },
    row: {
        flexDirection: 'row',
        alignItems: 'center',
    },
    alignCenter: { alignItems: 'center' },
    progress: { paddingLeft: 0 },
    iconContainerCircle: {
        alignItems: 'center',
        borderRadius: rem(22),
        width: rem(CIRCLE_SIZE),
        justifyContent: 'center',
        height: rem(CIRCLE_SIZE),
    },
    spaceUserAvatar: {
        paddingTop: rem(2),
    },
});
export default memo(RowPot);
